import React, {useEffect, useMemo} from 'react';
import {Alert} from 'antd';
import {useDispatch, useIntl, useModel, useSelector} from 'umi';
import ProForm, {ProFormSelect, ProFormSwitch, ProFormText} from '@ant-design/pro-form';
import {Form} from 'antd';
import {CheckAccess, hasAccess} from '@/utils/access';
import PERMISSIONS from '@/constants/permissions';
import * as UsersSelectors from '@/selectors/usersSelectors';
import * as PreferencesSelectors from '@/selectors/preferencesSelectors';
import {INTEGRATION_TYPE_MICROSOFT_SSO} from '@/utils/integrations';
import UserIntegrations from '@/components/UserDrawer/Edit/UserIntegrations';
import Office365MailUserPanel, { useHasOffice365MailUserPanel } from '@/components/UserDrawer/Edit/Office365MailUserPanel';
import { useManagedUserIntegrations } from '@/components/UserDrawer/Edit/UserIntegrations';
import styles from './UserDetails.module.less';
import {CustomColorPicker} from '@/components/Kanban/StateManager/LeadStateColor';
import {getIntlTranslation, INTL_ACTION, INTL_ENTITY} from '@/utils/intl';

interface UserDetailsProps {
  userData?: any;
  onChangeUser?: () => void;
  onFieldBlur?: (name: string, value: any) => void;
  isCreateMode?: boolean;
  useTransferableRoles?: boolean;
  disabled?: boolean;
}

const UserDetails: React.FC<UserDetailsProps> = ({userData, isCreateMode = false, onFieldBlur, useTransferableRoles = false, disabled = false}) => {
  const {initialState} = useModel('@@initialState');
  const intl = useIntl();
  const form = Form.useFormInstance();
  const hasRoleViewAccess = hasAccess(initialState.currentUser, [PERMISSIONS.Roles.VIEW, PERMISSIONS.BUSINESS_PARTNER])
  // office365_mail is excluded from the generic list on purpose, so its panel
  // rendering below is what makes that list's "no data" state a lie.
  const showsMailPanel = useHasOffice365MailUserPanel(userData?.id || userData?.uuid)
  const hasManagedIntegrations = useManagedUserIntegrations().length > 0

  const allRoles = useSelector(UsersSelectors.selectRoles);
  const transferableRoles = useSelector(UsersSelectors.selectTransferableRoles);

  const dispatch = useDispatch();
  const partnerUserIntegrations = useSelector(PreferencesSelectors.selectPartnerUserIntegrations);
  // While Microsoft SSO is enabled, roles are re-stamped from the Entra group
  // mapping on every sign-in — warn before someone hand-edits them.
  const rolesManagedBySso = Array.isArray(partnerUserIntegrations) &&
    partnerUserIntegrations.some((integration) => integration.type === INTEGRATION_TYPE_MICROSOFT_SSO);

  useEffect(() => {
    if (!partnerUserIntegrations)
      dispatch({type: 'preferences/fetchPartnerUserIntegrations'});
  }, []);

  const rolesOptions = useMemo(() => {
    if (!useTransferableRoles) {
      return (allRoles || []).map((role) => ({
        value: role.id,
        label: role.name,
      }));
    }

    const tRoles = transferableRoles || [];
    const tRoleIds = new Set(tRoles.map((r) => r.id));

    const options = tRoles.map((role) => ({
      value: role.id,
      label: role.name,
      disabled: false,
    }));

    if (userData?.roles) {
      userData.roles.forEach((role) => {
        if (!tRoleIds.has(role.id)) {
          options.push({
            value: role.id,
            label: role.name,
            disabled: true,
          });
        }
      });
    }

    return options;
  }, [allRoles, transferableRoles, useTransferableRoles, userData?.roles]);

  const getFieldProps = (name: string) => ({
    onBlur: async (e: React.FocusEvent<HTMLInputElement>) => {
      if (!onFieldBlur) return;
      try {
        await form.validateFields([name]);
        onFieldBlur(name, e.target.value);
      } catch {}
    },
  });

  return (
      <div>
        <ProFormText
          label={intl.formatMessage({
            id: 'pages.customers.firstName',
            defaultMessage: 'First Name',
          })}
          required
          name={'firstname'}
          rules={[{required: true}]}
          disabled={disabled}
          fieldProps={getFieldProps('firstname')}
        />
        <ProFormText
          label={intl.formatMessage({
            id: 'pages.customers.lastName',
            defaultMessage: 'Last Name',
          })}
          name={'lastname'}
          disabled={disabled}
          fieldProps={getFieldProps('lastname')}
        />

        <ProFormText
          name="email"
          required
          rules={[
            {
              required: true,
            },
            {
              type: 'email'
            }
          ]}
          label={intl.formatMessage({
            id: 'common.attribute.email',
            defaultMessage: 'Email',
          })}
          disabled={!isCreateMode || disabled}
          fieldProps={getFieldProps('email')}
        />
        {isCreateMode && (
          <ProFormSwitch
            name="send_invitation_email"
            initialValue={true}
            label={intl.formatMessage({
              id: 'pages.users.sendInvitationEmail',
              defaultMessage: 'Send invitation email',
            })}
            fieldProps={{disabled}}
          />
        )}
        <ProFormText
          name="phone"
          rules={[
            { max: 30 },
            {
              pattern:
                /^[+]?[0-9]{1,4}?[-.\s]?[(]?[0-9]{1,3}?[)]?[-.\s]?[0-9]{1,4}[-.\s]?[0-9]{1,4}[-.\s]?[0-9]{1,9}$/,
              message: intl.formatMessage({
                id: 'pages.users.phone.invalidFormat',
                defaultMessage: 'Please enter a valid phone number.',
              }),
            },
          ]}
          label={intl.formatMessage({
            id: 'common.attribute.phone',
            defaultMessage: 'Phone',
          })}
          disabled={disabled}
          fieldProps={getFieldProps('phone')}
        />
        <ProForm.Item
          name={'color'}
          label={intl.formatMessage({
            id: 'pages.calendar.custom.color',
            defaultMessage: 'Color',
          })}
        >
          <CustomColorPicker disabled={disabled} />
        </ProForm.Item>
        <ProFormText
          name="personal_id"
          label={intl.formatMessage({
            id: 'pages.users.attribute.personal_id',
            defaultMessage: 'Personal Id',
          })}
          disabled={disabled}
          fieldProps={getFieldProps('personal_id')}
        />
        {(hasRoleViewAccess || isCreateMode) && rolesManagedBySso && (
          <ProForm.Item>
            <Alert
              type="info"
              showIcon
              message={intl.formatMessage({
                id: 'pages.users.roles_managed_by_sso',
                defaultMessage:
                  "Roles for this workspace are managed by Microsoft Entra SSO — manual changes may be overwritten at the user's next sign-in.",
              })}
            />
          </ProForm.Item>
        )}
        {(hasRoleViewAccess || isCreateMode) && <ProFormSelect
          label={intl.formatMessage({
            id: 'entity.role.singular',
            defaultMessage: 'Role',
          })}
          name={'roles'}
          required
          mode="multiple"
          style={{width: '100%'}}
          placeholder={getIntlTranslation(intl, {
            action: INTL_ACTION.SELECT_PLURAL,
            entity: INTL_ENTITY.ROLE
          })}
          options={rolesOptions}
          rules={[{required: true}]}
          disabled={disabled}
        />}
        {!isCreateMode && (
          <>
            <ProFormText
              name="migration_id"
              label={intl.formatMessage({
                id: 'pages.users.migrationId',
                defaultMessage: 'Migration Id',
              })}
              disabled={disabled}
              fieldProps={getFieldProps('migration_id')}
            />
            <ProFormText
              name="migration_source"
              label={intl.formatMessage({
                id: 'pages.users.migrationSource',
                defaultMessage: 'Migration Source',
              })}
              disabled={disabled}
              fieldProps={getFieldProps('migration_source')}
            />
            <CheckAccess access={[PERMISSIONS.INTEGRATIONS.VIEW, PERMISSIONS.BUSINESS_PARTNER]}>
              <ProForm.Item
                name={"integrations"}
                label={intl.formatMessage({
                  id: 'pages.users.integrations',
                  defaultMessage: 'Integrations',
                })}
                className={!hasManagedIntegrations && showsMailPanel ? styles.integrationsFieldCollapsed : undefined}
              >
                <UserIntegrations
                  disabled={disabled}
                  userId={userData?.id}
                  showEmpty={!showsMailPanel}
                />
              </ProForm.Item>
              {/* Own panel, outside the `integrations` form field: a mailbox
                  assignment cannot be expressed as an {integration_type,
                  integration_id} pair, and that field is saved by delete-and-
                  recreate which would drop it. */}
              <Office365MailUserPanel
                userUuid={userData?.id || userData?.uuid}
                userEmail={userData?.email}
                disabled={disabled}
              />
            </CheckAccess>
          </>
        )}
    </div>
  );
};

export default UserDetails
